import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon } from "@fortawesome/free-solid-svg-icons";
import logo from '../assets/logo.png';

const DARK = {
  mode: "dark",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.08)",
  accentBgLight: "rgba(12,105,200,0.12)",
  accentBorder: "rgba(12,105,200,0.20)",
  bg: "#0A0A0A",
  bgCard: "#141414",
  bgAlt: "#111111",
  bgElevated: "#1E1E1E",
  border: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.12)",
  shadowMd: "0 4px 16px rgba(0,0,0,0.4)",
  text: "#FAFAFA",
  textSecondary: "rgba(255,255,255,0.70)",
  textMuted: "rgba(255,255,255,0.40)",
  headerBg: "rgba(10,10,10,0.92)",
  scrollbarThumb: "rgba(255,255,255,0.12)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
};

const LIGHT = {
  mode: "light",
  accent: "#0C69C8",
  accentBgSubtle: "rgba(12,105,200,0.05)",
  accentBgLight: "rgba(12,105,200,0.08)",
  accentBorder: "rgba(12,105,200,0.15)",
  bg: "#F5F7FA",
  bgCard: "#FFFFFF",
  bgAlt: "#EDF0F5",
  bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)",
  borderMedium: "rgba(10,29,68,0.12)",
  shadowMd: "0 4px 16px rgba(10,29,68,0.08)",
  text: "#0F1629",
  textSecondary: "#3D4A6B",
  textMuted: "#7A87A8",
  headerBg: "rgba(245,247,250,0.95)",
  scrollbarThumb: "rgba(10,29,68,0.12)",
  accentGrad: "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
};

const sections = [
  {
    num: "01", title: "Introduction",
    body: "This Privacy Policy describes how Snows ProAm collects, uses, shares, and protects personal data when you use our App, website, school challenges, and related services.",
  },
  {
    num: "02", title: "Information We Collect",
    body: null,
    bullets: [
      { label: "Personal Information", detail: "Name, email address, and other information you provide during registration." },
      { label: "School and Challenge Information", detail: "Student account type, school or institute membership, year group or team, step totals, challenge progress, and leaderboard position." },
      { label: "Health and Fitness Data", detail: "Step count, distance, calories, workouts, motion, and related activity data when you connect a supported health service or use activity tracking." },
      { label: "Geolocation Data", detail: "Location or route information when you give device permission and actively use a location-based feature." },
      { label: "Usage Data", detail: "Information about your use of the App, including the features you use and the time spent on the App." },
      { label: "Device and Advertising Data", detail: "Device or app identifiers, advertising identifiers where available, operating system, app version, and related technical information used for security, analytics, advertising measurement, and install attribution." },
      { label: "App Event Data", detail: "App activation, completed registration, and successful feature actions such as saving an activity, joining a club or session, or creating a community. We use event data to understand feature use and measure advertising performance." },
    ],
  },
  {
    num: "03", title: "Children and Student Accounts",
    body: "You must be at least 12 years old to use Snows ProAm. In Ireland, a child is a person under 18. We provide a higher level of protection for users who identify as secondary-school students and explain this processing in clear language for students, parents, guardians, and schools.",
    subSections: [
      {
        title: "Student safeguards",
        list: [
          "Student profiles stay in Ghost Mode and cannot share a live map location with other users.",
          "Students cannot access or upload Highlights, access public Communities or global chats, or add friends.",
          "Student leaderboard views are limited to student accounts. School leaderboards may show the student's name or username, school or year-group membership, step totals, progress, and rank to authorised participants or administrators.",
          "A route may still be processed privately when the student deliberately starts activity tracking and grants device permission. This is separate from live location sharing.",
        ],
      },
      {
        title: "Parents, guardians, and schools",
        list: [
          "Where we rely on consent to provide an online service directly to a child under 16 in Ireland, the consent must be authorised by a parent or guardian.",
          "Schools should give students and parents their own privacy information and identify the school's lawful basis, approval process, retention arrangements, and contact point.",
          "Children have their own data-protection rights. A parent or guardian does not automatically own a child's personal data, although they may help the child exercise those rights where appropriate.",
        ],
      },
    ],
    note: <>Read the plain-language <a href="/student-privacy" style={{ textDecoration: "underline" }}>Student Privacy &amp; Safety Notice</a>.</>,
  },
  {
    num: "04", title: "How We Use Your Information",
    body: "We use your information to:",
    list: [
      "Provide and improve our Service.",
      "Facilitate bookings and payments.",
      "Match you with other users for sports activities.",
      "Communicate with you about your account and bookings.",
      "Ensure the security of our App and users.",
      "Understand how the App is used and measure whether advertising on services such as Facebook and Instagram leads to installs, registrations, and successful in-app actions.",
    ],
  },
  {
    num: "05", title: "Sharing Your Information",
    body: "We may share your information with:",
    bullets: [
      { label: "Service Providers", detail: "Third-party companies that provide services on our behalf, such as payment processing and geolocation services." },
      { label: "Schools and Challenge Administrators", detail: "Information needed to organise a school programme, verify participation, provide support, and show eligible leaderboards, subject to access controls and each party's responsibilities." },
      { label: "Analytics and Advertising Partners", detail: "Google and its affiliates may process limited device, app, advertising, consent, network, diagnostic, and interaction data to serve and measure AdMob ads and prevent fraud. For adult accounts only, Meta Platforms Ireland Limited and its affiliates may receive permitted app-interaction, device, and advertising-identifier data through Meta App Events to measure advertising on Facebook and Instagram." },
      { label: "Law Enforcement", detail: "If required by law or if we believe that such action is necessary to comply with legal obligations or protect our rights." },
    ],
  },
  {
    num: "06", title: "Geolocation Data",
    body: "We use geolocation data to provide location-based services, such as finding nearby sports facilities and matching you with other users. Geolocation data is collected through your device's GPS when you give us permission.",
    subSections: [
      {
        title: "How We Request and Use Geolocation Data",
        list: [
          "Permission: We will request your permission to access your location the first time you use any location-based feature.",
          "Ongoing Consent: If you grant access, we collect your location data in real time while the app is open and in use.",
          "Background Location: If you start an activity-tracking session, location may continue to be processed while that session is active so route, pace, and distance can be recorded when the app is not on screen.",
          "Usage: We use your location to show nearby facilities, help match users by proximity, and improve venue suggestions.",
        ],
      },
      {
        title: "Managing Geolocation Permissions",
        list: [
          "You can disable geolocation services at any time through your device's settings. Doing so will limit location-dependent features.",
        ],
      },
      {
        title: "Public route discovery",
        list: [
          "New GPS activities are saved privately, and their full routes appear in your activity history. Adult account holders can choose to publish a route to the worldwide map; students cannot publish routes.",
          "Before publication, the app makes the original activity private and removes the first and last 200 metres from the public route copy. Places along the remaining route may still be recognisable.",
          "Other people can view and replay a published route with your username, activity title, date, sport, and distance. Published routes also contribute to community heatmaps, including areas where only one athlete has shared a route. Overlapping activity from more athletes increases the heat.",
          "You can remove a published route from discovery in Activity History. Previously viewed or saved copies outside Snows ProAm may remain with others.",
        ],
      },
    ],
  },
  {
    num: "07", title: "Use of Step Data for Fitness and Track Rankings",
    body: "If you use Fitness, Track, school challenge, or step-tracking features, step data is used to calculate progress and leaderboard rankings. The audience depends on the account and leaderboard type. Student accounts receive the additional restrictions described above.",
    subSections: [
      {
        title: "How Step Data is Used",
        list: [
          "Your steps are tracked and used to calculate your rank.",
          "Your ranking may be displayed regionally, nationally, and globally.",
          "Other eligible users and relevant institute administrators may view the profile and step information shown on a leaderboard.",
        ],
      },
    ],
    note: "You may contact support@snowsproam.com about leaderboard visibility, correction, restriction, or deletion, or delete your account through App Settings.",
  },
  {
    num: "08", title: "In-App Advertising, Consent, and Measurement",
    body: "The Snows ProAm mobile app shows Google AdMob banner and interstitial ads. We also use Meta App Events for adult accounts to understand whether advertising on Facebook or Instagram results in an installation, activation, registration, or another successful in-app action. Google and Meta process information under their own terms and privacy policies.",
    subSections: [
      {
        title: "Google AdMob ads",
        list: [
          "Google may process an IP address and approximate location, app interactions, ad views and clicks, diagnostics, performance or crash information, consent signals, and device, app, or advertising identifiers where permitted. Google uses this information to serve and measure ads, provide analytics, secure the service, and prevent fraud and abuse.",
          "Adults may receive personalised ads only where their region, Google consent choice, platform rules, and device settings permit it. Adults can reopen Google ad privacy choices from App Settings.",
          "Accounts under 18 are tagged for protected child or teen treatment, receive G-rated inventory, and are not offered personalised advertising. A user who declines personalisation can still use the App and may continue to see non-personalised or limited ads.",
        ],
      },
      {
        title: "Meta measurement for adults",
        list: [
          "Meta App Events and Meta advertising-identifier collection remain disabled until a completed profile shows the user is at least 18. Adult events may include app activation, registration, successful feature actions, and limited context such as sport, source screen, or session type.",
          "We do not intentionally include a name, email address (including an Apple Hide My Email address), phone number, chat message, payment credential, precise GPS location, activity route, food entry, step count, or health measurement in Meta custom-event parameters.",
          "Meta may receive standard device, app, operating-system, network, and advertising-identifier information when its SDK is enabled and the device, platform settings, and applicable law permit it.",
        ],
      },
      {
        title: "Your choices and sensitive-data limits",
        list: [
          "Where required, Google's consent form lets adults accept, refuse, or manage personalised-ad choices. Refusing consent never blocks use of the App.",
          "On iPhone, Snows ProAm asks adults for App Tracking Transparency permission before enabling advertiser tracking. Declining does not prevent App use, but limits identifier-based personalisation, attribution, and measurement.",
          "On Android, you can manage, reset, or delete your advertising identifier through your device's privacy or ads settings. Available controls vary by Android version and device manufacturer.",
          "We do not provide health or fitness data, HealthKit or Health Connect data, exact step totals, GPS routes, precise location, private messages, user content, payment details, names, or email addresses to Google or Meta for personalised advertising.",
        ],
      },
    ],
    note: <>
      Learn how Google uses partner app data in <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Google's partner technologies notice</a>, read the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Google Privacy Policy</a>, and read the <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Meta Privacy Policy</a>. Where consent is required, you may withdraw it without affecting earlier lawful processing.
    </>,
  },
  {
    num: "09", title: "Data Security",
    body: "We take the security of your information very seriously and employ industry-standard measures. While no method of transmission or storage can be guaranteed to be completely secure, we are continuously improving our protocols.",
  },
  {
    num: "10", title: "Data Retention",
    body: "We retain your information for as long as your account is active or as needed to provide the Service. We may also retain information as required by law or to enforce our agreements. Third parties such as Google and Meta retain information under their own policies and legal obligations.",
  },
  {
    num: "11", title: "Your Rights",
    body: "You have the right to:",
    list: [
      "Access, correct, or delete your personal information.",
      "Object to or restrict processing.",
      "Withdraw your consent at any time.",
      "Complain to the Irish Data Protection Commission if you believe your data-protection rights have been infringed.",
    ],
    note: "Contact us at support@snowsproam.com to exercise your rights.",
  },
  {
    num: "12", title: "Account and Data Deletion",
    body: "You can request deletion of your Snows ProAm account and the associated personal data without needing to contact us first.",
    subSections: [
      {
        title: "Delete your account in the App",
        list: [
          "Sign in to Snows ProAm and open Settings.",
          "Select Delete Account, type DELETE when prompted, and confirm the request.",
          "The App sends an authenticated deletion request, removes your sign-in account and the account data covered by our deletion process, signs you out, and clears locally stored app data from that device.",
        ],
      },
      {
        title: "If you cannot access the App",
        list: [
          "Email support@snowsproam.com from the email address associated with your account and use the subject “Account deletion request”.",
          "We may ask for limited information to verify that you control the account before completing the request.",
        ],
      },
      {
        title: "What is deleted or retained",
        list: [
          "We delete the authentication account, profile information, and account-linked sports, social, fitness, notification, team, and community records covered by our deletion process. Shared content may be deleted or de-identified where reasonably possible.",
          "We may retain limited payment and transaction records, tax or accounting records, fraud-prevention records, safety and moderation records, and records required to establish or defend legal claims. These records are restricted to those purposes and are normally retained for up to six years after account closure, or longer only where applicable law or an active legal matter requires it.",
          "Information already received by a third party, including Google or Meta, is processed under that third party's privacy policy and controls. Deleting your Snows ProAm account stops future account activity, but it does not automatically delete information held independently by those third parties.",
        ],
      },
    ],
    note: "Account deletion is permanent. If you only want specific information corrected or deleted, contact support@snowsproam.com instead of deleting the entire account.",
  },
  {
    num: "13", title: "Changes to This Privacy Policy",
    body: "We may update this Privacy Policy from time to time. Continued use of the app after changes means you accept the new policy.",
  },
  {
    num: "14", title: "Contact Us",
    body: "If you have any questions about this Privacy Policy, contact us at support@snowsproam.com.",
  },
];

const PrivacyPolicy = () => {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });
  const C = isDark ? DARK : LIGHT;

  const toggleTheme = useCallback(() => {
    setIsDark((v) => {
      const next = !v;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);

  return (
    <div style={{
      background: C.bg, minHeight: "100vh", color: C.text,
      fontFamily: "'DM Sans', sans-serif",
      transition: "background 0.3s ease, color 0.3s ease",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; -webkit-font-smoothing: antialiased; }
        a { color: inherit; text-decoration: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.scrollbarThumb}; border-radius: 99px; }
        .theme-toggle { transition: transform 0.2s ease; }
        .theme-toggle:hover { transform: rotate(15deg) scale(1.1); }
        .section-card { transition: border-color 0.2s ease, box-shadow 0.2s ease; }
        .section-card:hover { border-color: ${C.accentBorder} !important; box-shadow: ${C.shadowMd} !important; }
        .grad-text {
          background: linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
      `}</style>

      {/* Header */}
      <header style={{
        position: "sticky", top: 0, zIndex: 100,
        height: 64, padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: C.headerBg,
        backdropFilter: "blur(20px) saturate(1.2)",
        WebkitBackdropFilter: "blur(20px) saturate(1.2)",
        borderBottom: `1px solid ${C.border}`,
        transition: "background 0.3s ease",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center" }}>
          <img src={logo} alt="Snows ProAm" style={{ height: 30 }} />
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <a href="/" style={{
            fontSize: 13.5, fontWeight: 500, color: C.textSecondary,
            padding: "6px 14px", borderRadius: 8,
            border: `1px solid ${C.border}`, background: C.bgElevated,
          }}>← Back</a>
          <button className="theme-toggle" onClick={toggleTheme}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: C.bgElevated, border: `1px solid ${C.border}`,
              color: C.text, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 13 }} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "64px 24px 48px" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
          fontSize: 12, fontWeight: 600, color: C.accent,
          textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 20,
        }}>Legal</div>

        <h1 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "clamp(36px, 6vw, 56px)",
          fontWeight: 800, letterSpacing: -1.2, lineHeight: 1.05,
          marginBottom: 16, color: C.text,
        }}>
          Privacy <span className="grad-text">Policy</span>
        </h1>

        <p style={{ fontSize: 16, color: C.textSecondary, lineHeight: 1.6, maxWidth: 520 }}>
          We're committed to protecting your data. Here's exactly how we collect, use, and safeguard your information.
        </p>

        <div style={{
          marginTop: 24, display: "flex", gap: 16, flexWrap: "wrap",
          fontSize: 13, color: C.textMuted, fontWeight: 500,
        }}>
          <span>Effective: 27 September 2026</span>
          <span style={{ color: C.border }}>·</span>
          <span>Governed by Irish Law</span>
        </div>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 100px" }}>
        <div style={{ display: "grid", gap: 12 }}>
          {sections.map((s) => (
            <div key={s.num} className="section-card" style={{
              background: C.bgCard,
              border: `1px solid ${C.border}`,
              borderRadius: 16, padding: "28px 28px",
              position: "relative", overflow: "hidden",
            }}>
              {/* Ghost number */}
              <div style={{
                position: "absolute", top: 12, right: 20,
                fontSize: 56, fontWeight: 800, fontFamily: "'Sora', sans-serif",
                color: isDark ? "rgba(255,255,255,0.03)" : "rgba(12,105,200,0.05)",
                lineHeight: 1, userSelect: "none",
              }}>{s.num}</div>

              <div style={{
                fontSize: 11, fontWeight: 700, color: C.accent,
                textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10,
              }}>{s.num}</div>

              <h3 style={{
                fontFamily: "'Sora', sans-serif",
                fontSize: 17, fontWeight: 700, color: C.text,
                marginBottom: 12, letterSpacing: -0.2,
              }}>{s.title}</h3>

              {s.body && (
                <p style={{ fontSize: 14.5, color: C.textSecondary, lineHeight: 1.7, marginBottom: s.bullets || s.list || s.subSections ? 14 : 0 }}>
                  {s.body}
                </p>
              )}

              {/* Bullet points with labels */}
              {s.bullets && (
                <div style={{ display: "grid", gap: 10, marginBottom: s.subSections ? 16 : 0 }}>
                  {s.bullets.map((b) => (
                    <div key={b.label} style={{
                      padding: "12px 14px", borderRadius: 10,
                      background: C.bgAlt, border: `1px solid ${C.border}`,
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{b.label}: </span>
                      <span style={{ fontSize: 13.5, color: C.textSecondary, lineHeight: 1.6 }}>{b.detail}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Simple list */}
              {s.list && (
                <div style={{ display: "grid", gap: 6, marginBottom: s.note ? 14 : 0 }}>
                  {s.list.map((item) => (
                    <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <div style={{
                        width: 5, height: 5, borderRadius: "50%", background: C.accent,
                        flexShrink: 0, marginTop: 8,
                      }} />
                      <span style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.65 }}>{item}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Sub-sections */}
              {s.subSections && (
                <div style={{ display: "grid", gap: 14, marginBottom: s.note ? 14 : 0 }}>
                  {s.subSections.map((sub) => (
                    <div key={sub.title} style={{
                      padding: "14px 16px", borderRadius: 12,
                      background: C.bgAlt, border: `1px solid ${C.border}`,
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>{sub.title}</div>
                      <div style={{ display: "grid", gap: 6 }}>
                        {sub.list.map((item) => (
                          <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <div style={{
                              width: 5, height: 5, borderRadius: "50%", background: C.accent,
                              flexShrink: 0, marginTop: 8,
                            }} />
                            <span style={{ fontSize: 13.5, color: C.textSecondary, lineHeight: 1.6 }}>{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Note */}
              {s.note && (
                <div style={{
                  padding: "12px 14px", borderRadius: 10,
                  background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
                  fontSize: 13, color: C.textSecondary, lineHeight: 1.6, fontStyle: "italic",
                }}>{s.note}</div>
              )}
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div style={{
          marginTop: 40, padding: "24px 28px",
          background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`,
          borderRadius: 16, fontSize: 14, color: C.textSecondary, lineHeight: 1.65,
        }}>
          <span style={{ fontWeight: 700, color: C.accent }}>Questions?</span>{" "}
          If you have any questions about this Privacy Policy, contact us at{" "}
          <a href="mailto:support@snowsproam.com" style={{ color: C.accent, fontWeight: 600 }}>
            support@snowsproam.com
          </a>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
